// src/pages/Users/Techniques/components/TechniqueSheet.jsx

import React, { useEffect } from "react";

import {
  FaExternalLinkAlt,
  FaTimes,
  FaVideo,
  FaVolumeUp,
} from "react-icons/fa";

import useTechniqueSpeech from "../hooks/useTechniqueSpeech";

import {
  FALLBACK_TECHNIQUE_IMG,
  beltTheme,
  cleanTechniqueText,
  resolveTechniqueAssetUrl,
} from "../utils/techniquesUtils";

import { speakJapanese } from "../utils/speechUtils";

function handleImageError(event) {
  const image = event.currentTarget;

  if (
    image.dataset.fallbackApplied ===
    "true"
  ) {
    return;
  }

  image.dataset.fallbackApplied =
    "true";

  image.src =
    FALLBACK_TECHNIQUE_IMG;
}

export default function TechniqueSheet({
  technique,
  onClose,
}) {
  const {
    image1_url,
    nom,
    nom_japonais,
    traduction,
    famille,
    ceinture,
    video_url,
    information,
  } = technique || {};

  const imageUrl =
    resolveTechniqueAssetUrl(
      image1_url,
    ) || FALLBACK_TECHNIQUE_IMG;

  const videoUrl =
    resolveTechniqueAssetUrl(
      video_url,
    );

  const theme =
    beltTheme(ceinture);

  const speechText =
    useTechniqueSpeech(
      nom_japonais || nom,
    );

  const cleanInformation =
    cleanTechniqueText(information);

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  const openVideo = () => {
    if (!videoUrl) {
      return;
    }

    window.open(
      videoUrl,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Fiche technique ${nom || ""}`}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
    >
      <article
        onClick={(event) =>
          event.stopPropagation()
        }
        className="absolute inset-x-0 bottom-0 max-h-[94dvh] overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-900 sm:relative sm:inset-auto sm:w-full sm:max-w-5xl sm:rounded-[2rem]"
      >
        <header
          className={`relative overflow-hidden bg-gradient-to-r ${theme.gradient} px-4 py-4 text-white sm:px-6`}
        >
          <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/20 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ring-1 ${theme.badge}`}
              >
                {ceinture || "Ceinture"}
              </span>

              <h2 className="mt-2 line-clamp-2 text-xl font-black leading-tight sm:text-2xl">
                {nom}
              </h2>

              {traduction ? (
                <p className="mt-1 text-sm italic text-white/85">
                  {traduction}
                </p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/15 backdrop-blur transition hover:bg-white/25 active:scale-95"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="max-h-[calc(94dvh-116px)] overflow-y-auto">
          <div className="grid gap-5 p-4 sm:p-6 lg:grid-cols-[minmax(320px,0.9fr)_minmax(0,1.1fr)]">
            <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
              <div className="aspect-[551/617] w-full">
                <img
                  src={imageUrl}
                  alt={
                    nom ||
                    "Technique de judo"
                  }
                  onError={
                    handleImageError
                  }
                  className="h-full w-full object-contain p-2"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-2 gap-3">
                {famille ? (
                  <InfoCard
                    label="Famille"
                    value={cleanTechniqueText(
                      famille,
                    )}
                  />
                ) : null}

                {ceinture ? (
                  <InfoCard
                    label="Ceinture"
                    value={cleanTechniqueText(
                      ceinture,
                    )}
                  />
                ) : null}
              </div>

              {cleanInformation ? (
                <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                    Informations
                  </p>

                  <p className="mt-2 whitespace-pre-line text-sm leading-7 text-slate-700 dark:text-slate-300">
                    {cleanInformation}
                  </p>
                </div>
              ) : (
                <div className="mt-4 rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-5 text-sm italic text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                  Aucune information complémentaire pour cette technique.
                </div>
              )}

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    speakJapanese(
                      speechText,
                    )
                  }
                  disabled={!speechText}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-200 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <FaVolumeUp />
                  Prononcer
                </button>

                {videoUrl ? (
                  <button
                    type="button"
                    onClick={openVideo}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    <FaVideo />
                    Vidéo
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 active:scale-95 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    <FaExternalLinkAlt />
                    Fermer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-sm font-black text-slate-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}